export type PortDir = 'in' | 'out';
export type PortKind =
  | 'trigger'
  | 'value'
  | 'event'
  | 'command'
  | 'subscribe'
  | 'host'
  | 'read'
  | 'write'
  | 'dev'
  | 'peer';

export type CatalogPort = {
  id: string;
  dir: PortDir;
  kind: PortKind;
  labelKey: string;
  /** Overrides `flow.port.${labelKey}` when set (relay channel names). */
  label?: string;
  n?: number;
};

export const RELAY_WIDGET_CHANNELS = 8;

export type PortExtra = {
  poll?: boolean;
  widgetType?: string;
  busType?: string;
  role?: string;
  deviceType?: string;
  channelNames?: string[];
  channelReadOnly?: boolean[];
  icon?: string;
};

export function portsForNode(type: string, extra?: PortExtra): CatalogPort[] {
  if (type === 'controller') {
    return [{ id: 'host', dir: 'out', kind: 'host', labelKey: 'host' }];
  }
  if (type === 'bus') {
    const ports: CatalogPort[] = [
      { id: 'host', dir: 'in', kind: 'host', labelKey: 'host' },
      { id: 'read', dir: 'out', kind: 'read', labelKey: 'read' },
      { id: 'write', dir: 'in', kind: 'write', labelKey: 'write' },
    ];
    if (extra?.busType === 'modbus') {
      ports.push({ id: 'peer', dir: extra?.role === 'master' ? 'in' : 'out', kind: 'peer', labelKey: 'peer' });
    }
    return ports;
  }
  if (type === 'device') {
    if (extra?.deviceType === 'stacklight') {
      return [
        { id: 'host', dir: 'in', kind: 'host', labelKey: 'host' },
        { id: 'out', dir: 'out', kind: 'dev', labelKey: 'dev' },
        { id: 'in', dir: 'in', kind: 'command', labelKey: 'in' },
      ];
    }
    const ports: CatalogPort[] = [
      { id: 'read', dir: 'in', kind: 'read', labelKey: 'read' },
      { id: 'write', dir: 'out', kind: 'write', labelKey: 'write' },
      { id: 'out', dir: 'out', kind: 'dev', labelKey: 'dev' },
      { id: 'in', dir: 'in', kind: 'command', labelKey: 'in' },
    ];
    // GPIO and the radio sit on the controller, same as a stacklight. The
    // host edge is how that link is drawn; without the port it disappears.
    if (extra?.deviceType === 'zigbee'
      || extra?.deviceType === 'gpio_relay'
      || extra?.deviceType === 'gpio_led'
      || extra?.deviceType === 'gpio_buzzer') {
      ports.unshift({ id: 'host', dir: 'in', kind: 'host', labelKey: 'host' });
    }
    return ports;
  }
  if (type === 'timer') {
    return [{ id: 'trigger', dir: 'out', kind: 'trigger', labelKey: 'poll' }];
  }
  if (type === 'point') {
    const topology: CatalogPort = { id: 'dev', dir: 'in', kind: 'dev', labelKey: 'dev' };
    if (extra?.poll === false) {
      return [
        topology,
        { id: 'event', dir: 'out', kind: 'event', labelKey: 'rx' },
        { id: 'value', dir: 'out', kind: 'value', labelKey: 'value' },
      ];
    }
    if (extra?.poll === undefined) {
      return [
        topology,
        { id: 'command', dir: 'in', kind: 'command', labelKey: 'in' },
        { id: 'value', dir: 'out', kind: 'value', labelKey: 'value' },
      ];
    }
    // Polled and still writable (Modbus coils): timer on trigger, actuator on command.
    return [
      topology,
      { id: 'trigger', dir: 'in', kind: 'trigger', labelKey: 'poll' },
      { id: 'command', dir: 'in', kind: 'command', labelKey: 'in' },
      { id: 'value', dir: 'out', kind: 'value', labelKey: 'value' },
    ];
  }
  if (type === 'calc') {
    return [
      { id: 'in', dir: 'in', kind: 'value', labelKey: 'in' },
      { id: 'out', dir: 'out', kind: 'value', labelKey: 'out' },
    ];
  }
  if (type === 'threshold') {
    return [
      { id: 'in', dir: 'in', kind: 'subscribe', labelKey: 'in' },
      { id: 'out', dir: 'out', kind: 'value', labelKey: 'out' },
      { id: 'event', dir: 'out', kind: 'event', labelKey: 'event' },
    ];
  }
  if (type === 'notify') {
    return [
      { id: 'event', dir: 'in', kind: 'event', labelKey: 'event' },
    ];
  }
  if (type === 'toggle') {
    return [
      { id: 'in', dir: 'in', kind: 'event', labelKey: 'event' },
      { id: 'out', dir: 'out', kind: 'command', labelKey: 'command' },
    ];
  }
  if (type === 'label') {
    return [{ id: 'value', dir: 'in', kind: 'value', labelKey: 'value' }];
  }
  if (type === 'mqtt_topic' || type === 'zigbee_point') {
    return [
      { id: 'publish', dir: 'in', kind: 'value', labelKey: 'publish' },
      { id: 'subscribe', dir: 'out', kind: 'subscribe', labelKey: 'subscribe' },
    ];
  }
  if (type === 'dashboard') {
    if (extra?.widgetType === 'temperature') {
      return [
        { id: 'in', dir: 'in', kind: 'subscribe', labelKey: 'temp' },
        { id: 'inRh', dir: 'in', kind: 'subscribe', labelKey: 'rh' },
        { id: 'command', dir: 'out', kind: 'command', labelKey: 'command' },
      ];
    }
    if (extra?.widgetType === 'sml_meter') {
      return [
        { id: 'in', dir: 'in', kind: 'subscribe', labelKey: 'energy' },
        { id: 'power', dir: 'in', kind: 'subscribe', labelKey: 'power' },
        { id: 'reverse', dir: 'in', kind: 'subscribe', labelKey: 'reverse' },
        { id: 'command', dir: 'out', kind: 'command', labelKey: 'command' },
      ];
    }
    if (extra?.widgetType === 'stacklight') {
      return [
        { id: 'in', dir: 'in', kind: 'subscribe', labelKey: 'value' },
      ];
    }
    if (extra?.widgetType === 'switch') {
      const bulb = extra.icon === 'bulb';
      return [
        { id: 'in', dir: 'in', kind: 'subscribe', labelKey: bulb ? 'indicator' : 'switch' },
      ];
    }
    if (extra?.widgetType === 'relay') {
      const names = Array.isArray(extra.channelNames) ? extra.channelNames : [];
      const readOnly = Array.isArray(extra.channelReadOnly) ? extra.channelReadOnly : [];
      const ports: CatalogPort[] = [];
      for (let n = 1; n <= RELAY_WIDGET_CHANNELS; n += 1) {
        const name = String(names[n - 1] || '').trim();
        const bulb = readOnly[n - 1] === true;
        ports.push({
          id: `ch${n}`,
          dir: 'in',
          kind: 'subscribe',
          labelKey: bulb ? 'channelBulb' : 'channelSwitch',
          n,
          label: name || undefined,
        });
      }
      return ports;
    }
    return [
      { id: 'in', dir: 'in', kind: 'subscribe', labelKey: 'value' },
      { id: 'command', dir: 'out', kind: 'command', labelKey: 'command' },
    ];
  }
  return [];
}

export function portsCompatible(fromKind: PortKind, toKind: PortKind): boolean {
  if (fromKind === toKind) {
    return true;
  }
  if (fromKind === 'value' && (toKind === 'value' || toKind === 'command' || toKind === 'subscribe')) {
    return true;
  }
  if (fromKind === 'dev' && toKind === 'command') {
    return true;
  }
  if (fromKind === 'command' && toKind === 'value') {
    return true;
  }
  if (fromKind === 'event' && (toKind === 'value' || toKind === 'command' || toKind === 'event')) {
    return true;
  }
  if (fromKind === 'command' && toKind === 'command') {
    return true;
  }
  if (fromKind === 'subscribe' && (toKind === 'subscribe' || toKind === 'value' || toKind === 'event')) {
    return true;
  }
  return false;
}
